/**
 * DOM node-range reconciliation.
 *
 * Node parts preserve their comment boundaries while switching among text,
 * raw HTML, nested templates, arrays, keyed repeats, conditionals, DOM nodes,
 * legacy components, cached template switches, and async/priority values.
 * Ordinary dynamic values always become Text nodes; only branded raw HTML and
 * the isolated component adapter enter the parser. Keyed repeats reuse
 * compatible template instances and move only nodes that are out of place,
 * preserving focus, selection, and scroll state; the same reuse trick now
 * applies to a plain (non-repeated) node part receiving successive instances
 * of the same template (G14).
 *
 * `cache()` (G13) keeps a per-part map of every template instance ever shown,
 * keyed by template identity: switching away detaches nodes without
 * destroying the instance, and switching back re-inserts and updates it.
 *
 * Async values and `until()` (G7) commit nothing synchronously beyond a
 * placeholder; every `updateNodePart` call bumps a per-part epoch so a
 * promise settling after the part has moved on to other content is
 * detectably stale and never mutates the DOM.
 */
import { isRawHtml } from '../ui/html-escape.js';
import { destroyMaterialized, destroyRepeatEntry, insertBeforeEnd, rangeParentOf, removeRange, releasePlacedFragment, unmountInstanceIfOwned, } from './render-dom-range.js';
import { logRuntimeWarn } from '../utils/runtime-logger.js';
import { conditional, isCache, isConditional, isLive, isNode, isRepeat, isTemplateInstanceValue, isUntil, resolveValue, textValue, } from './render-values.js';
import { isLegacyComponent, materializeLegacyComponent } from './render-component-adapter.js';
import { updateRepeatPart } from './render-dom-repeat.js';
let nodeAdopter = null;
export function setNodeAdopter(adopter) {
    nodeAdopter = adopter;
}
function isThenableValue(value) {
    return !!(value &&
        typeof value === 'object' &&
        typeof value.then === 'function');
}
/**
 * Public entry point: every EXTERNAL update (an effect re-run over new
 * bindings, a repeat/cache reconciliation) bumps the part's epoch, proving
 * any async work armed by an earlier external update stale (G7). Internal
 * recursion — conditional branch selection, or an async/`until()` settle
 * committing its own already-decided result — must NOT bump the epoch again,
 * or a sibling `until()` promise that is still legitimately pending would be
 * wrongly invalidated by its own sibling's settle. That recursion goes
 * through `dispatchNodeValue` directly instead.
 */
export function updateNodePart(part, value, options = {}) {
    part.asyncEpoch = (part.asyncEpoch ?? 0) + 1;
    // Hydration (#887): the first commit of a part whose range already holds
    // server-rendered DOM adopts those nodes rather than rebuilding them. The
    // flag clears either way, so a declined adoption falls straight through to
    // the ordinary commit below and every later update is unaffected.
    if (part.hydrating) {
        part.hydrating = false;
        if (nodeAdopter?.(part, value))
            return;
    }
    dispatchNodeValue(part, value, options);
}
function dispatchNodeValue(part, value, options = {}) {
    const resolvedRaw = resolveValue(value);
    // Node parts unwrap `live()` transparently — the live/current-DOM-state
    // bypass is specific to property/attribute parts (G9).
    const resolved = isLive(resolvedRaw) ? resolveValue(resolvedRaw.value) : resolvedRaw;
    const partValue = part.spec.type === 'conditional' && !options.fromConditional && !isConditional(resolved)
        ? conditional(resolved, part.spec.truthy ?? '', part.spec.falsy ?? '')
        : resolved;
    if (isRepeat(partValue)) {
        updateRepeatPart(part, partValue);
        return;
    }
    if (isConditional(partValue)) {
        dispatchNodeValue(part, resolveValue(partValue.condition) ? partValue.truthy : partValue.falsy, {
            fromConditional: true,
        });
        return;
    }
    for (const entry of part.entries?.values() ?? []) {
        destroyRepeatEntry(entry);
    }
    part.entries?.clear();
    part.entryOrder = undefined;
    if (isCache(partValue)) {
        updateCachePart(part, partValue.value);
        return;
    }
    // Any non-cache destination invalidates cache tracking; the actual DOM
    // detach happens through the normal range-clearing paths below.
    part.activeCacheTemplate = undefined;
    if (isUntil(partValue)) {
        updateUntilPart(part, partValue.values);
        return;
    }
    if (isThenableValue(partValue)) {
        updateAsyncPart(part, partValue);
        return;
    }
    commitPlainNodeValue(part, partValue);
}
/** The non-branded, non-async rendering path: text, raw HTML, nodes, instances, arrays. */
function commitPlainNodeValue(part, partValue) {
    const shouldUseRaw = part.spec.type === 'raw-html' && isRawHtml(partValue);
    const text = !shouldUseRaw &&
        !isRawHtml(partValue) &&
        !isTemplateInstanceValue(partValue) &&
        !isLegacyComponent(partValue) &&
        !isNode(partValue) &&
        !Array.isArray(partValue);
    if (text) {
        // Text values retain one Text node and update only its data.
        const nextText = textValue(partValue);
        if (!part.textNode) {
            destroyMaterialized(part.materialized);
            removeRange(part);
            if (!part.documentObj)
                return;
            part.textNode = part.documentObj.createTextNode('');
            part.nodes = [part.textNode];
            insertBeforeEnd(part, part.nodes);
            part.materialized = null;
        }
        part.textNode.data = nextText;
        return;
    }
    // Reuse an existing instance of the same template (G14): update in place
    // instead of destroy+rebuild, so focus, selection, and scroll survive.
    if (isTemplateInstanceValue(partValue) &&
        part.materialized?.instance &&
        part.materialized.instance.template === partValue.template) {
        part.textNode = null;
        part.materialized.instance.update(partValue.bindings);
        return;
    }
    part.textNode = null;
    destroyMaterialized(part.materialized);
    removeRange(part);
    if (!part.documentObj || !part.domAdapter)
        return;
    let valueToMaterialize = partValue;
    if (!shouldUseRaw && part.spec.type === 'raw-html') {
        valueToMaterialize = textValue(partValue);
    }
    const materialized = materializeValue(valueToMaterialize, part.documentObj, part.domAdapter, shouldUseRaw);
    part.materialized = materialized;
    part.nodes = materialized.nodes;
    insertBeforeEnd(part, materialized.nodes);
    const placedIn = rangeParentOf(part);
    if (placedIn)
        releasePlacedFragment(materialized, placedIn);
    materialized.mount?.();
}
/**
 * `cache(value)` (G13): non-instance values flow through the ordinary path
 * unchanged; template instances are tracked per template identity so
 * switching away detaches nodes (kept in the map) instead of destroying the
 * instance, and switching back re-inserts and updates it.
 */
function updateCachePart(part, innerValue) {
    const resolvedInner = resolveValue(innerValue);
    if (!isTemplateInstanceValue(resolvedInner)) {
        part.activeCacheTemplate = undefined;
        commitPlainNodeValue(part, resolvedInner);
        return;
    }
    const cacheMap = part.cacheMap ?? (part.cacheMap = new Map());
    const template = resolvedInner.template;
    if (part.activeCacheTemplate === undefined) {
        // First cache use on this part: clear whatever ordinary content it held.
        part.textNode = null;
        destroyMaterialized(part.materialized);
        part.materialized = null;
        removeRange(part);
    }
    else if (part.activeCacheTemplate !== template) {
        // Detach the currently shown cached entry without destroying it.
        const activeEntry = cacheMap.get(part.activeCacheTemplate);
        if (activeEntry) {
            for (const node of activeEntry.nodes)
                node.parentNode?.removeChild(node);
        }
    }
    let entry = cacheMap.get(template);
    if (entry) {
        entry.instance.update(resolvedInner.bindings);
        insertBeforeEnd(part, entry.nodes);
    }
    else {
        if (!part.documentObj)
            return;
        const mounted = mountInstanceNodes(resolvedInner, part.documentObj, false);
        insertBeforeEnd(part, mounted.nodes);
        // Spend the private fragment here too (#1335). `cache()` holds its entries
        // for the life of the part, so a fragment kept here is kept for the life of
        // the part — one per cached template, plus whatever a consumed parse
        // brought with it. `owner` then follows the instance's own container, so
        // the epoch-less fallback still compares against the right thing.
        const placedIn = rangeParentOf(part);
        if (placedIn)
            resolvedInner.adoptPlacement?.(placedIn);
        entry = {
            instance: resolvedInner,
            nodes: mounted.nodes,
            owner: (resolvedInner.container ?? mounted.owner),
            epoch: mounted.epoch,
        };
        cacheMap.set(template, entry);
    }
    part.activeCacheTemplate = template;
    part.nodes = entry.nodes;
}
/**
 * Async node values (G7): a thenable renders nothing until it settles. A
 * settle only commits if the part is still alive and no newer update has
 * moved the part on to something else since (`asyncEpoch`). Rejections are
 * logged once and leave the range empty. Hydration's adopted ranges (#931)
 * skip the placeholder commit — the server's markup already IS the visible
 * content — through `commitPlaceholder = false`.
 */
function updateAsyncPart(part, thenable, commitPlaceholder = true) {
    const epoch = part.asyncEpoch ?? 0;
    if (commitPlaceholder)
        commitPlainNodeValue(part, '');
    thenable.then((resolved) => {
        if (part.destroyed || part.asyncEpoch !== epoch)
            return;
        // Committing the settled value is still part of THIS epoch, not a new
        // external update — go through dispatchNodeValue so the epoch does not
        // advance and orphan any other pending work sharing this epoch.
        dispatchNodeValue(part, resolved);
    }, (error) => {
        if (part.destroyed || part.asyncEpoch !== epoch)
            return;
        console.error(error);
    });
}
/**
 * `until(...values)` (G7): the first non-thenable value renders immediately
 * (or empty if every value is a thenable). Every thenable arms a settle that
 * commits its value only if its index is lower (higher priority) than
 * whatever index is currently committed, so a low-priority placeholder never
 * clobbers a higher-priority result that already landed. Hydration's adopted
 * ranges (#931) skip the placeholder commit — the server's markup already IS
 * the visible content — through `commitPlaceholder = false`; priority
 * bookkeeping (`untilIndex`) and the armed settles run unchanged.
 */
function updateUntilPart(part, rawValues, commitPlaceholder = true) {
    const resolvedValues = rawValues.map((raw) => resolveValue(raw));
    let immediateIndex = resolvedValues.length;
    let immediateValue = '';
    for (let index = 0; index < resolvedValues.length; index += 1) {
        const candidate = resolvedValues[index];
        if (!isThenableValue(candidate)) {
            immediateIndex = index;
            immediateValue = candidate;
            break;
        }
    }
    part.untilIndex = immediateIndex;
    if (commitPlaceholder)
        commitPlainNodeValue(part, immediateValue);
    const epoch = part.asyncEpoch ?? 0;
    resolvedValues.forEach((candidate, index) => {
        if (!isThenableValue(candidate))
            return;
        candidate.then((resolved) => {
            if (part.destroyed || part.asyncEpoch !== epoch)
                return;
            if (index >= (part.untilIndex ?? resolvedValues.length))
                return;
            part.untilIndex = index;
            // Same epoch as the sibling values armed alongside this one — see
            // updateAsyncPart's dispatchNodeValue comment.
            dispatchNodeValue(part, resolved);
        }, (error) => {
            console.error(error);
        });
    });
}
/**
 * Arms the settle path for an async hole whose server-rendered range was just
 * adopted (#931). The server's markup is already the visible content, so the
 * placeholder commit is skipped; priority, epoch staleness, and the settled
 * swap behave exactly as the mount path's. Called by the hydration adopter
 * after it has bound the part to the server's nodes.
 */
export function armAdoptedAsyncCommit(part, partValue) {
    if (isUntil(partValue)) {
        updateUntilPart(part, partValue.values, false);
        return;
    }
    if (isThenableValue(partValue))
        updateAsyncPart(part, partValue, false);
}
/**
 * Mounts a template instance into a detached fragment and returns everything
 * it produced. Returning only `instance.nodes` drops content when the
 * instance's own root is itself a binding (#554): its `nodes` then holds only
 * that binding's boundary comments while the rendered content sits between
 * them as separate fragment children. Ownership is proved by `mountEpoch`
 * (#1335), stamped by this mount and captured alongside the nodes: the
 * fragment used to be that proof, and after placement `container` points at
 * the parent while this materialization still owns the instance. `owner`
 * survives only as the fallback for an implementation that keeps no epoch.
 * An instance that is already mounted elsewhere is moved — a single instance
 * owns a single set of DOM nodes, so its previous location renders empty.
 * That move is legitimate during handover (a new parent materializes a
 * pre-built instance before the old parent's teardown runs) but invisible
 * when accidental, so it is surfaced through the runtime logger.
 */
function mountInstanceNodes(instance, documentObj, reused) {
    if (instance.container !== null && instance.container !== undefined) {
        logRuntimeWarn('Hydrogen: a TemplateInstance already mounted elsewhere was interpolated again; ' +
            'it moves to the new location and its previous location renders empty. ' +
            'Create one instance per interpolation site if both must render.');
    }
    // The clone's own fragment is already a detached parent, so an instance that
    // can hand it back saves relocating every one of its nodes into a second,
    // empty one.
    //
    // The fast path does not go through `mount`, and `mount` is a replaceable
    // instance member: `withComponentApi` wraps it to run a component's setup
    // once the DOM exists, and `suspense` wraps it to swap in a fallback. Taking
    // the fast path on a wrapped instance would silently skip that wrapper — a
    // mini-map that never attaches its pointer handlers, a suspense boundary
    // that never shows. An OWN `mount` is exactly what a wrapper leaves behind,
    // so it is what disqualifies the instance from the shortcut (#1334).
    const own = Object.hasOwn(instance, 'mount') ? undefined : instance.mountOwnFragment?.(reused);
    const owner = own ?? documentObj.createDocumentFragment();
    if (own === undefined)
        instance.mount(owner);
    // `instance.nodes` is the clone's children as the mount captured them, so
    // unless the mount added something at the fragment root, walking the live
    // NodeList again yields the same nodes in the same order at DOM-binding cost
    // per node. A root-level node part commits BETWEEN its own markers, and those
    // markers are themselves in `instance.nodes`, so anything it adds appears
    // here as extra children; a fresh mount's ranges start empty, so nothing is
    // ever removed. Equal counts therefore prove the two lists hold the same
    // nodes, and the #554 case (an instance whose root IS a binding) is exactly
    // the unequal one that still walks.
    const rendered = owner.childNodes.length === instance.nodes.length
        ? instance.nodes
        : Array.from(owner.childNodes);
    // The equal-count branch hands back `instance.nodes` ITSELF. `unmount`
    // detaches that array rather than draining it, so a holder keeps the nodes
    // it was handed and #1332 — a materialization emptied out from under itself
    // by an unmount — cannot happen through the alias. Nothing mutates a node
    // list in place; every writer assigns a new array. The unequal-count branch
    // is a fresh array either way, and an empty render falls back to the same
    // (empty) instance list.
    return {
        nodes: rendered.length > 0 ? rendered : instance.nodes,
        owner,
        epoch: instance.mountEpoch,
    };
}
export function materializeValue(value, documentObj, domAdapter, forceRaw = false, reused = false) {
    const resolved = resolveValue(value);
    if (forceRaw || isRawHtml(resolved)) {
        // Raw parsing is restricted to the explicit trusted-markup contract.
        const html = isRawHtml(resolved)
            ? resolved.html
            : String(resolved?.html ?? resolved ?? '');
        return { nodes: domAdapter.fragmentFromHtml(html, documentObj) };
    }
    if (isTemplateInstanceValue(resolved)) {
        // Nested instances retain their own bindings and unmount lifecycle. The
        // cleanup is ownership-guarded (#626): if the instance has since been
        // mounted under a new parent, this stale materialization must not tear
        // it out of its new home.
        const mounted = mountInstanceNodes(resolved, documentObj, reused);
        // With a mount identity to compare, the guard needs no reference to the
        // fragment at all — and NOT capturing it is the point: the closure outlives
        // placement, so a captured fragment is a retained one (#1335). Only an
        // instance implementation with no epoch still needs the container.
        const epoch = mounted.epoch;
        const owner = epoch === undefined ? mounted.owner : null;
        return {
            nodes: mounted.nodes,
            instance: resolved,
            fragment: mounted.owner,
            cleanup: () => unmountInstanceIfOwned(resolved, owner, epoch),
        };
    }
    if (isLegacyComponent(resolved)) {
        return materializeLegacyComponent(resolved, documentObj, domAdapter);
    }
    if (Array.isArray(resolved)) {
        // Composite values preserve child mount and cleanup ordering.
        const children = resolved.map((item) => materializeValue(item, documentObj, domAdapter, false, reused));
        return {
            nodes: children.flatMap((child) => child.nodes),
            // Named, not just flattened: placement has to reach each child's own
            // instance to spend its fragment (#1335).
            children,
            mount() {
                for (const child of children)
                    child.mount?.();
            },
            cleanup() {
                for (const child of children)
                    destroyMaterialized(child);
            },
        };
    }
    if (isNode(resolved))
        return { nodes: [resolved] };
    return { nodes: [documentObj.createTextNode(textValue(resolved))] };
}
//# sourceMappingURL=render-dom-nodes.js.map